import mqtt from 'mqtt';
import getMqttConfig from '../config/mqttConfig';

/**
 * MQTT Service — singleton for managing MQTT client connections.
 * Handles connection, subscription, publishing, and reconnection.
 */
class MqttService {
  constructor() {
    this.client = null;
    this.isConnected = false;
    this.subscriptions = new Map(); // topic -> Set of callbacks
    this.wildcardRegexMap = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.config = null;
    this.connectionListeners = new Set();
    this.errorListeners = new Set();
    this.isConnecting = false;
  }

  isWildcardTopic(topic) {
    return typeof topic === 'string' && (topic.includes('+') || topic.includes('#'));
  }

  compileWildcardRegex(pattern) {
    const regexPattern = pattern
      .replace(/\//g, '\\/')
      .replace(/\+/g, '[^/]+')
      .replace(/#/g, '.*');
    return new RegExp(`^${regexPattern}$`);
  }

  /**
   * Initialize MQTT connection
   */
  connect(onConnect, onError) {
    if (this.client && this.isConnected) {
      if (onConnect) onConnect();
      return;
    }

    if (this.isConnecting) return;
    this.isConnecting = true;

    // Clean up existing client
    if (this.client) {
      try {
        this.client.removeAllListeners();
        this.client.end(true);
      } catch (error) {
        console.error('MQTT: Error cleaning up existing client:', error);
      }
      this.client = null;
      this.isConnected = false;
    }

    try {
      this.config = getMqttConfig();
      const connectUrl = `${this.config.protocol}://${this.config.host}:${this.config.port}${this.config.path}`;

      this.client = mqtt.connect(connectUrl, this.config.options);

      this.client.on('connect', () => {
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Re-subscribe to all previous subscriptions
        this.subscriptions.forEach((callbacks, topic) => {
          if (this.client) {
            this.client.subscribe(topic, (err) => {
              if (err) console.error(`MQTT: Error resubscribing to ${topic}:`, err);
            });
          }
        });

        this.connectionListeners.forEach((listener) => listener(true));
        if (onConnect) onConnect();
      });

      this.client.on('error', (error) => {
        console.error('MQTT: Error', error.message);
        this.isConnecting = false;
        this.errorListeners.forEach((listener) => listener(error));
        if (onError) onError(error);
      });

      this.client.on('close', () => {
        this.isConnected = false;
        this.isConnecting = false;
        this.connectionListeners.forEach((listener) => listener(false));
      });

      this.client.on('reconnect', () => {
        this.reconnectAttempts++;
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('MQTT: Max reconnection attempts reached');
          this.disconnect();
        }
      });

      this.client.on('offline', () => {
        this.isConnected = false;
        this.connectionListeners.forEach((listener) => listener(false));
      });

      // Message handler
      this.client.on('message', (topic, message) => {
        const messageString = message.toString();
        if (this.subscriptions.size === 0) return;

        const matchingCallbacks = [];

        const exactCallbacks = this.subscriptions.get(topic);
        if (exactCallbacks) {
          exactCallbacks.forEach((callback) =>
            matchingCallbacks.push({ callback, subscriptionTopic: topic })
          );
        }

        if (this.wildcardRegexMap.size > 0) {
          this.wildcardRegexMap.forEach((regex, subscriptionTopic) => {
            if (regex.test(topic)) {
              const callbacks = this.subscriptions.get(subscriptionTopic);
              callbacks?.forEach((callback) => {
                matchingCallbacks.push({ callback, subscriptionTopic });
              });
            }
          });
        }

        if (matchingCallbacks.length === 0) return;

        let parsedMessage;
        try {
          parsedMessage = JSON.parse(messageString);
        } catch {
          parsedMessage = messageString;
        }

        matchingCallbacks.forEach(({ callback }) => {
          try {
            callback(parsedMessage, topic);
          } catch (error) {
            console.error('MQTT: Error in callback:', error);
          }
        });
      });
    } catch (error) {
      console.error('MQTT: Failed to create client', error);
      this.isConnecting = false;
      if (onError) onError(error);
    }
  }

  disconnect() {
    if (this.client) {
      this.client.end();
      this.client = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.connectionListeners.forEach((listener) => listener(false));
    }
  }

  subscribe(topic, callback, options = {}) {
    if (!topic || !callback) {
      console.error('MQTT: Topic and callback are required for subscription');
      return;
    }

    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic).add(callback);

    if (this.isWildcardTopic(topic) && !this.wildcardRegexMap.has(topic)) {
      this.wildcardRegexMap.set(topic, this.compileWildcardRegex(topic));
    }

    if (this.client && this.isConnected) {
      this.client.subscribe(topic, options, (err) => {
        if (err) console.error(`MQTT: Error subscribing to ${topic}:`, err);
      });
    }
  }

  unsubscribe(topic, callback) {
    if (!this.subscriptions.has(topic)) return;

    const callbacks = this.subscriptions.get(topic);

    if (callback) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.subscriptions.delete(topic);
        this.wildcardRegexMap.delete(topic);
        if (this.client && this.isConnected) {
          this.client.unsubscribe(topic, (err) => {
            if (err) console.error(`MQTT: Error unsubscribing from ${topic}:`, err);
          });
        }
      }
    } else {
      this.subscriptions.delete(topic);
      this.wildcardRegexMap.delete(topic);
      if (this.client && this.isConnected) {
        this.client.unsubscribe(topic, (err) => {
          if (err) console.error(`MQTT: Error unsubscribing from ${topic}:`, err);
        });
      }
    }
  }

  addConnectionListener(listener) {
    this.connectionListeners.add(listener);
  }

  removeConnectionListener(listener) {
    this.connectionListeners.delete(listener);
  }

  addErrorListener(listener) {
    this.errorListeners.add(listener);
  }

  removeErrorListener(listener) {
    this.errorListeners.delete(listener);
  }
}

// Export singleton instance
const mqttService = new MqttService();
export default mqttService;

